export type InputValueType = string | number;

/**
 * The subset of a KeyboardEvent that {@link nudge} consults. Taking this
 * rather than the event keeps the stepping logic testable without
 * synthesizing events.
 */
export interface NudgeModifiers {
    altKey?: boolean;
    shiftKey?: boolean;
}

export interface NudgeStep {
    /** How much a single press moves the value. */
    increment: number;

    /**
     * How many decimals the result keeps. Deliberately not derived from
     * `increment`: it is what the value snaps to, not what the step needs.
     */
    decimals: number;
}

/**
 * Arrow-key stepping for numeric inputs, keyed by modifier.
 *
 * Rounding to a fixed number of decimals does two jobs. It stops repeated
 * presses accumulating float error - unrounded, ten alt-presses from 0 land
 * on 0.9999999999999999 - and it snaps the value onto the step's grid, so a
 * finer value entered by hand is discarded rather than carried along. That
 * snapping follows the convention set by Photoshop, Illustrator and Figma,
 * and is a deliberate change from the behavior this replaced, which
 * preserved every decimal the user had typed.
 */
export const NUDGE_STEPS: Record<'alt' | 'shift' | 'none', NudgeStep> = {
    alt: { increment: 0.1, decimals: 1 },
    shift: { increment: 10, decimals: 0 },
    none: { increment: 1, decimals: 0 },
};

export function nudgeStepFor(modifiers: NudgeModifiers): NudgeStep {
    // alt outranks shift, preserving the precedence of the code this
    // replaced. Holding both steps by 0.1.
    if (modifiers.altKey) return NUDGE_STEPS.alt;
    if (modifiers.shiftKey) return NUDGE_STEPS.shift;
    return NUDGE_STEPS.none;
}

/**
 * Steps `value` one press in `direction`, rounded to the precision the
 * modifier selects.
 *
 * Non-finite inputs are propagated rather than rejected - NaN in, NaN out -
 * so callers that care should check before calling.
 */
export function nudge(
    value: number,
    direction: 1 | -1,
    modifiers: NudgeModifiers
): number {
    const { increment, decimals } = nudgeStepFor(modifiers);
    return Number((value + direction * increment).toFixed(decimals));
}

export interface PercentStepperHelperOptions {
    home: number;
    min: number;
    max: number;
    step: number;
}

export function percentStepperHelper({
    home,
    min,
    max,
    step,
}: PercentStepperHelperOptions) {
    function clamp(value: number): number {
        if (isNaN(value)) return home;
        if (value < min) return min;
        if (value > max) return max;
        return value;
    }

    function importValue(unitValue: number | undefined): number {
        return unitValue == null ? home : clamp(Math.round(unitValue * 100));
    }

    function formatter(value: InputValueType | undefined): string {
        if (value == null) return '';
        const nValue = typeof value === 'string' ? parser(value) : value;
        if (isNaN(nValue)) return 'NaN';
        if (!isFinite(nValue)) return `${nValue < 0 ? '-' : ''}∞`;
        return `${nValue}%`;
    }

    function parser(displayValue: string | undefined): number {
        if (displayValue == null) return home;

        // strip leading and trailing % signs
        displayValue = displayValue.replace(/%/g, ' ').trim();

        // if this turns up a match, there was an interior % sign (or a word break)
        const spacePos = displayValue.indexOf(' ');

        // ...so drop everything after that point
        if (spacePos >= 0) displayValue = displayValue.slice(0, spacePos);

        // if that results in an empty string, bail
        if (!displayValue) return home;

        return clamp(+displayValue);
    }

    function exportValue(componentValue: number): number {
        return componentValue / 100;
    }

    return {
        min,
        max,
        step,
        importValue,
        formatter,
        parser,
        exportValue,
    };
}
